/**
 * Decoded-pixel stack cache with a single priority queue.
 *
 * PROBLEM WE KEEP HITTING — the upstream `vpd-dicomweb` Zig binary
 * serves each HTTP request from a single event loop. It's actually
 * fast once it owns the loop (≈60 ms per 500 KB slice inside the
 * cluster), but it can't overlap I/O between requests. With three
 * replicas behind a Service, at most three requests serve in
 * parallel; anything above that queues *inside the pod* and
 * eventually starves the liveness probe → pod killed → the browser
 * sees long tail latencies and 502s.
 *
 * We therefore cap concurrency strictly at MAX_CONCURRENT (= replica
 * count). All three callers — the active-slice fetcher, the
 * foreground prefetch, and the background warmer — enqueue through
 * the same gate, with priorities:
 *
 *   0  user-facing active slice          (never waits behind anything)
 *   1  foreground prefetch  (±radius)   (jumps over warmer)
 *   2  warmer               (spiral)    (idle-time backlog)
 *
 * Every request keys by SOP Instance UID: duplicate requests collapse
 * to the same promise so rapid scroll clicks don't flood the queue.
 *
 * Pixel buffers are decoded off the main thread via `decodeWorker.ts`.
 */

import type { DecodedPixels } from "../core/dicom";
import type { StudyInstance } from "../api/client";
import { fetchInstanceBytes } from "../api/client";
import { parseDicom, decodePixels } from "../core/dicom";

// -------- tuning --------
const DEFAULT_MAX_ENTRIES = 200;   // cap on decoded slices held in RAM
const MAX_CONCURRENT      = 3;     // matches dicomweb replica count
const WORKER_POOL_SIZE    = 2;

// -------- LRU cache --------
const pixelsByUid:  Map<string, DecodedPixels>          = new Map();
const inflightByUid: Map<string, Promise<DecodedPixels>> = new Map();

// -------- telemetry (read by StudyViewer HUD). Using signals so
//  the HUD JSX re-renders every time any counter updates. Previously
//  these were plain JS fields and the HUD showed stale values
//  between Solid re-render triggers. --------
import { createSignal } from "solid-js";
const [hits,       setHits]       = createSignal(0);
const [misses,     setMisses]     = createSignal(0);
const [fetchMsSig, setFetchMs]    = createSignal(0);
const [decodeMsSig,setDecodeMs]   = createSignal(0);
const [workerOk,   setWorkerOk]   = createSignal(false);
const [cacheSize,  setCacheSize]  = createSignal(0);
const [inflightN,  setInflightN]  = createSignal(0);
const [warmerOn,   setWarmerOn]   = createSignal(false);

function pushCacheSize()  { setCacheSize(pixelsByUid.size); }
function pushInflight()   { setInflightN(inflightByUid.size); }

export const stats = {
  get hits()      { return hits(); },
  get misses()    { return misses(); },
  get fetchMs()   { return fetchMsSig(); },
  get decodeMs()  { return decodeMsSig(); },
  get workerOk()  { return workerOk(); },
  cacheSize:  () => cacheSize(),
  inflight:   () => inflightN(),
  warmer:     () => ({ active: warmerOn(), queued: queue.length }),
};

function touch(uid: string, px: DecodedPixels) {
  if (pixelsByUid.has(uid)) pixelsByUid.delete(uid);
  pixelsByUid.set(uid, px);
  while (pixelsByUid.size > DEFAULT_MAX_ENTRIES) {
    const firstKey = pixelsByUid.keys().next().value as string | undefined;
    if (!firstKey) break;
    pixelsByUid.delete(firstKey);
  }
  pushCacheSize();
}

export function resetStackCache() {
  pixelsByUid.clear();
  inflightByUid.clear();
  queue.length = 0;
  warmerGeneration++;
  warmerActive = false;
  pushCacheSize();
  pushInflight();
  setWarmerOn(false);
}

export function cachedPixels(uid: string | undefined): DecodedPixels | null {
  if (!uid) return null;
  const hit = pixelsByUid.get(uid);
  if (hit) setHits(hits() + 1); else setMisses(misses() + 1);
  return hit ?? null;
}

// ---------------------------------------------------------------------
//  Priority queue — ordered list, lowest number = highest priority.
//  Multiple entries can share a priority; ties break by insertion.
// ---------------------------------------------------------------------
interface QItem {
  priority: number;   // 0=active, 1=prefetch, 2=warmer
  uid:      string;
  inst:     StudyInstance;
  resolve:  (px: DecodedPixels) => void;
  reject:   (e: Error) => void;
}
let queue: QItem[] = [];
let active = 0;

function enqueue(item: QItem) {
  // Insert keeping queue sorted by priority (stable).
  let i = queue.length;
  while (i > 0 && queue[i - 1].priority > item.priority) i--;
  queue.splice(i, 0, item);
  drain();
}

function drain() {
  while (active < MAX_CONCURRENT && queue.length > 0) {
    const item = queue.shift()!;
    active++;
    loadOne(item).finally(() => { active--; drain(); });
  }
}

async function loadOne(item: QItem): Promise<void> {
  try {
    const t0 = performance.now();
    const bytes   = await fetchInstanceBytes(item.inst.studyUid, item.inst.seriesUid, item.inst.instanceUid);
    const t1 = performance.now();
    const decoded = await decodeInWorker(bytes);
    const t2 = performance.now();
    setFetchMs(t1 - t0);
    setDecodeMs(t2 - t1);
    touch(item.uid, decoded);
    item.resolve(decoded);
  } catch (err) {
    item.reject(err as Error);
  } finally {
    inflightByUid.delete(item.uid);
    pushInflight();
  }
}

/** Load a slice or return the cached copy. `priority` defaults to 0
 *  (highest) — callers that want to enqueue as background work pass
 *  higher numbers. */
export function getOrLoad(inst: StudyInstance, priority = 0): Promise<DecodedPixels> {
  const hit = pixelsByUid.get(inst.instanceUid);
  if (hit) { touch(inst.instanceUid, hit); return Promise.resolve(hit); }

  const already = inflightByUid.get(inst.instanceUid);
  if (already) return already;

  const p = new Promise<DecodedPixels>((resolve, reject) => {
    enqueue({ priority, uid: inst.instanceUid, inst, resolve, reject });
  });
  inflightByUid.set(inst.instanceUid, p);
  pushInflight();
  return p;
}

// ---------------------------------------------------------------------
//  Prefetch (priority 1) and Warmer (priority 2)
// ---------------------------------------------------------------------
export function prefetchNeighbours(list: StudyInstance[], activeIdx: number, radius = 4): void {
  if (!list.length) return;
  const targets: StudyInstance[] = [];
  for (let d = 1; d <= radius; d++) {
    const f = list[activeIdx + d];
    if (f) targets.push(f);
    const b = list[activeIdx - d];
    if (b) targets.push(b);
  }
  for (const inst of targets) {
    if (pixelsByUid.has(inst.instanceUid)) continue;
    if (inflightByUid.has(inst.instanceUid)) continue;
    getOrLoad(inst, 1).catch(() => { /* best effort */ });
  }
}

let warmerGeneration = 0;
let warmerActive     = false;
export function warmSeries(list: StudyInstance[], activeIdx: number): void {
  if (warmerActive) return;
  const gen = warmerGeneration;
  warmerActive = true; setWarmerOn(true);
  void (async () => {
    try {
      const order: number[] = [];
      for (let d = 0; d < list.length; d++) {
        const f = activeIdx + d, b = activeIdx - d;
        if (d === 0) order.push(activeIdx);
        else {
          if (f < list.length) order.push(f);
          if (b >= 0 && b !== f) order.push(b);
        }
      }
      for (const i of order) {
        if (gen !== warmerGeneration) return;
        const inst = list[i];
        if (!inst) continue;
        if (pixelsByUid.has(inst.instanceUid)) continue;
        if (inflightByUid.has(inst.instanceUid)) continue;
        try { await getOrLoad(inst, 2); } catch { /* best-effort */ }
      }
    } finally {
      warmerActive = false; setWarmerOn(false);
    }
  })();
}

export function cancelWarmer(): void {
  warmerGeneration++;
  warmerActive = false;
}

// ---------------------------------------------------------------------
//  Web-Worker pool — decode off the main thread.
// ---------------------------------------------------------------------
let workerPool: Worker[] | null = null;
let workerRR = 0;
const workerPending   = new Map<string, (px: DecodedPixels) => void>();
const workerRejecters = new Map<string, (e: Error) => void>();

function ensureWorkers() {
  if (workerPool !== null) return;
  try {
    workerPool = [];
    for (let i = 0; i < WORKER_POOL_SIZE; i++) {
      const w = new Worker(
        new URL("./decodeWorker.ts", import.meta.url),
        { type: "module" },
      );
      w.onmessage = (e: MessageEvent) => {
        const { id, ok, decoded, error } = e.data;
        const resolve = workerPending.get(id);
        const reject  = workerRejecters.get(id);
        workerPending.delete(id); workerRejecters.delete(id);
        if (!resolve) return;
        if (ok) resolve(decoded);
        else    reject?.(new Error(error || "decode failed"));
      };
      w.onerror = (err) => {
        console.error("[stackCache] worker error", err);
        setWorkerOk(false);
      };
      workerPool.push(w);
    }
    setWorkerOk(workerPool.length > 0);
    console.log(`[stackCache] ${workerPool.length} decode workers started`);
  } catch (err) {
    console.error("[stackCache] worker init failed, falling back", err);
    workerPool = [];
    stats.workerOk = false;
  }
}

async function decodeInWorker(bytes: ArrayBuffer): Promise<DecodedPixels> {
  ensureWorkers();
  if (!workerPool || workerPool.length === 0) {
    const parsed = parseDicom(bytes);
    return decodePixels(parsed);
  }
  const w = workerPool[workerRR++ % workerPool.length];
  const id = crypto.randomUUID();
  return new Promise<DecodedPixels>((resolve, reject) => {
    workerPending.set(id, resolve);
    workerRejecters.set(id, reject);
    w.postMessage({ id, bytes }, [bytes]);
  });
}

// -------- dev-tools hook --------
if (typeof window !== "undefined") {
  (window as any).__vpStackCache = {
    size:     () => pixelsByUid.size,
    inflight: () => inflightByUid.size,
    queue:    () => queue.length,
    stats,
  };
}
